import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../environments/environment';
import { ConsentService } from '../consent/consent.service';

type GtagFn = (...args: unknown[]) => void;

/**
 * Chargement conditionnel de Google Analytics (gtag.js).
 *
 * Deux verrous cumulatifs — GA ne se charge que si les DEUX sont ouverts :
 * 1. GA_MEASUREMENT_ID non vide : injecté au runtime par docker/entrypoint.sh
 *    uniquement sur le déploiement production. Dev/sandbox/intégration ne
 *    définissent pas la variable → aucun script GA, aucun appel réseau Google.
 * 2. Consentement "statistics" accordé via ConsentService (doctrine CNIL :
 *    opt-in strict, aucun tag de mesure d'audience avant accord explicite).
 *
 * Si l'utilisateur retire son consentement après chargement, le kill-switch
 * officiel `window['ga-disable-<ID>']` stoppe tout envoi et les cookies _ga*
 * sont purgés (le script lui-même ne peut pas être déchargé sans reload).
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly consentService = inject(ConsentService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly measurementId = environment.GA_MEASUREMENT_ID;
  private scriptInjected = false;

  constructor() {
    if (!this.isBrowser || !this.measurementId) {
      return;
    }

    effect(() => {
      if (this.consentService.statisticsAllowed()) {
        this.enableTracking();
      } else {
        this.disableTracking();
      }
    });
  }

  /** Réactive l'envoi et, au premier accord, injecte gtag.js + le suivi SPA. */
  private enableTracking(): void {
    (window as any)[`ga-disable-${this.measurementId}`] = false;

    if (this.scriptInjected) {
      return;
    }
    this.scriptInjected = true;

    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.gtag = function gtag() {
      w.dataLayer.push(arguments);
    } as GtagFn;

    w.gtag('js', new Date());
    // send_page_view: false — les page_view partent exclusivement via le
    // suivi SPA ci-dessous, sinon la vue initiale serait comptée deux fois.
    w.gtag('config', this.measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    // Vue courante (le consentement arrive souvent après la 1re navigation)…
    this.trackPageView(this.router.url);
    // …puis chaque navigation du router (SPA : pas de rechargement de page).
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.trackPageView(event.urlAfterRedirects));
  }

  private trackPageView(path: string): void {
    (window as any).gtag?.('event', 'page_view', {
      page_path: path,
      page_location: window.location.origin + path,
    });
  }

  /** Kill-switch GA + purge best-effort des cookies _ga / _ga_*. */
  private disableTracking(): void {
    (window as any)[`ga-disable-${this.measurementId}`] = true;

    const hostname = window.location.hostname;
    Object.keys(this.cookieService.getAll())
      .filter(name => name === '_ga' || name.startsWith('_ga_'))
      .forEach(name => {
        this.cookieService.delete(name, '/');
        this.cookieService.delete(name, '/', hostname);
        this.cookieService.delete(name, '/', `.${hostname}`);
      });
  }
}
